import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { getAppointmentTimeWithTimezone } from 'components/Assessments/helpers'
import { convertAppointmentTimeFormDataToFlatAnswers } from 'components/Assessments/helpers.uncontrolled'
import { AssessmentOverviewFormField } from './formField'
import { FuaAssessmentQuestionnaireItem } from '../../types/questionnaire'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Assessment Overview subsection adapter for FUA Assessment
 * Handles appointment time extraction and transformation
 */
export class AssessmentOverviewAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract appointment time with timezone support
    const appointmentTime = getAppointmentTimeWithTimezone(
      flatAnswersArray,
      options?.caregiverTimezone
    )

    // Extract reason for documentation
    const reasonForDocumentation = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_REASON_FOR_DOCUMENTATION
    )?.at(0)

    // Extract in-person status
    const inPerson = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_IN_PERSON
    )?.at(0)

    // Extract effective date (get raw string to preserve UTC timezone)
    const effectiveDateString = getAnswerValuesFromItems(
      flatAnswersArray,
      FuaAssessmentQuestionnaireItem.ASSESSMENT_EFFECTIVE_DATE,
      true // rawAnswer = true to get the string, not a dayjs object
    )?.at(0)

    // Extract effective date as dayjs object with timezone conversion
    const effectiveDate = effectiveDateString
      ? options?.caregiverTimezone
        ? dayjs.utc(effectiveDateString).tz(options.caregiverTimezone).startOf('day')
        : dayjs.utc(effectiveDateString).startOf('day')
      : undefined

    // Extract revisable care plan reference
    const revisableCarePlan =
      getAnswerValuesFromItems(
        flatAnswersArray,
        FuaAssessmentQuestionnaireItem.ASSESSMENT_REVISABLE_CARE_PLAN
      )?.at(0) || undefined

    return {
      [AssessmentOverviewFormField.APPOINTMENT_TIME]: appointmentTime,
      [AssessmentOverviewFormField.REASON_FOR_DOCUMENTATION]: reasonForDocumentation,
      [AssessmentOverviewFormField.IN_PERSON]: inPerson,
      [AssessmentOverviewFormField.EFFECTIVE_DATE]: effectiveDate,
      [AssessmentOverviewFormField.REVISABLE_CARE_PLAN]: revisableCarePlan
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build appointment time mutations
    if (this.formDataFieldExists(AssessmentOverviewFormField.APPOINTMENT_TIME)) {
      const appointmentTime = this.getFormDataField(AssessmentOverviewFormField.APPOINTMENT_TIME)
      if (appointmentTime) {
        const { appointmentTimeValues } =
          convertAppointmentTimeFormDataToFlatAnswers(appointmentTime)
        mutations.push(
          {
            linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_APPOINTMENT_TIME_START,
            value: appointmentTimeValues[0]
          },
          {
            linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_APPOINTMENT_TIME_END,
            value: appointmentTimeValues[1]
          }
        )
      }
    }

    // Build reason for documentation mutation
    if (this.formDataFieldExists(AssessmentOverviewFormField.REASON_FOR_DOCUMENTATION)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_REASON_FOR_DOCUMENTATION,
        value: this.getFormDataField(AssessmentOverviewFormField.REASON_FOR_DOCUMENTATION)
      })
    }

    // Build in-person mutation
    if (this.formDataFieldExists(AssessmentOverviewFormField.IN_PERSON)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_OVERVIEW_IN_PERSON,
        value: this.getFormDataField(AssessmentOverviewFormField.IN_PERSON)
      })
    }

    // Build effective date mutation
    if (this.formDataFieldExists(AssessmentOverviewFormField.EFFECTIVE_DATE)) {
      const effectiveDate = this.getFormDataField(AssessmentOverviewFormField.EFFECTIVE_DATE)
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_EFFECTIVE_DATE,
        value: effectiveDate?.utc().toISOString()
      })
    }

    // Build revisable care plan mutation
    if (this.formDataFieldExists(AssessmentOverviewFormField.REVISABLE_CARE_PLAN)) {
      const revisableCarePlan = this.getFormDataField(
        AssessmentOverviewFormField.REVISABLE_CARE_PLAN
      )

      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.ASSESSMENT_REVISABLE_CARE_PLAN,
        value: revisableCarePlan
      })
    }

    return mutations
  }
}
